import {
  asArray,
  isPlainObject,
  deepClone,
  reflect,
  doit,
  begin,
  generateUUID,
  equalContents,
  transactionHelper,
} from "./functions"
import {
  setSession,
  getSession,
  getSessionJSON,
  setSessionJSON,
  removeSession,
  setLocal,
  getLocal,
  setLocalJSON,
  getLocalJSON,
  removeLocal,
} from "./objectStore"
import {
  mergeIfEmptyIn,
  mergeIfEmpty,
  mergeIfUndefinedIn,
  mergeIfUndefined,
} from "./rubyPrototype"
import * as constants from "./constants"
import deepDiff from "deep-diff"
import { Turbo } from "@hotwired/turbo-rails"
import { Modal as FlowbiteModal, Tooltip as FlowbiteTooltip } from "flowbite"
import {
  formHelper,
  queryStringify,
  queryStringParse,
  queryStringAssign,
  plainFormData,
  railsParamsStyleToPlainData,
} from "./formHelper"
import {
  turboFrameTransform,
  turboFrameReload,
  turboLocationReload,
  actionCableHelper,
  formToFetchConfig,
  jsonFetch,
  htmlFetch,
  turboRequest,
  useCatchResponseError,
} from "./turboHelper"
import Alpine from "alpinejs"
import {
  selectElement,
  selectElements,
  selectParentElement,
  dispatchElement,
  handleElement,
  customDataset,
  downflow,
  upflow,
  provideElement,
  consumeElement,
  // getFocusInIndex, 
  // setFocusInIndex, 
  valueOfXNode,
  setValueOfXNode,
} from "./domHelper"
import { useVisibility } from "./domVisibilityHelper"
import {
  useAttributesObserver,
  useInputComposition,
  debounce,
} from "./domInputHelper"
import {
  createApp,
  reactive
} from '@moreonion/petite-vue'
import {
  kst,
  asLuxon,
  distanceOfTimeInWordsWithDirection,
  distanceOfDateInWordsWithDirection,
} from "./timeHelper"


// petite-vue로 서버사이드 렌더를 유사하게 구현합니다. vRenderFragment는 공개되는 함수는 아닙니다.
function parseHTML(html) {
  const container = document.createElement('div');
  container.innerHTML = html;
  return Array.from(container.children)
}

function vRenderFragment(html, data) {
  const frag = document.createDocumentFragment();
  const container = document.createElement('div');
  container.innerHTML = html;
  frag.appendChild(container);
  createApp(data).mount(container);
  return container
}

function vRenderNode(html, data) {
  return Array.from(vRenderFragment(html, data).children)
}

function vRenderHTML(html, data) {
  return vRenderFragment(html, data).innerHTML
}


// deprecated ( baseKits나 baseKit로 접근하도록 권장함 )
export {
  selectElement,
  getFocusInIndex,
  setFocusInIndex,
} from "./domHelper"

/**
 * URL 쿼리 파라미터를 로드하고, 선택적으로 특정 키의 값만 추출합니다.
 *
 * @param {string|number|string[]|number[]|object} [selectKeys] - 추출할 키 또는 키와 타입(key::type) 목록.
 *   - 문자열 또는 숫자인 경우: 해당 키의 값을 추출합니다.
 *   - 배열인 경우: 각 키에 해당하는 값을 추출합니다. 배열 요소는 문자열, 숫자 또는 'key::type' 형태일 수 있습니다.
 *   - 객체인 경우: 객체의 키들을 `selectKeys`로 사용합니다. 이 경우, 객체의 값은 무시됩니다.
 *   - 배열인 경우: 각 키에 해당하는 값을 추출합니다. 배열 요소는 문자열, 숫자 또는 'key::type' 형태일 수 있습니다.
 *   - 'key::type' 형태에서 type은 'number', 'boolean', 'string' 중 하나이며, 해당 타입으로 값을 변환합니다.
 *     - number: 숫자로 변환하며, 변환 실패 시 null을 반환합니다.
 *     - boolean: 불리언으로 변환하며, "false" 문자열은 false로, 그 외는 true로 변환합니다.
 *     - string: (기본값) 문자열로 유지합니다.
 * @returns {object} - 선택된 키와 값의 객체 또는 전체 파라미터 객체
 */
export function loadParams(selectKeys) {
  const loadedParams = queryStringParse(window.location.search.slice(1))
  if (!Array.isArray(selectKeys) && typeof selectKeys === "object" && selectKeys !== null) {
    selectKeys = Object.keys(selectKeys)
  }
  if (typeof selectKeys === "string" || typeof selectKeys === "number") {
    selectKeys = [String(selectKeys)]
  }
  if (Array.isArray(selectKeys)) {
    const selectedValues = {}
    selectKeys.forEach((key) => {
      if (typeof key === "number") {
        key = String(key)
      }
      if (typeof key === "string") {
        const { selectKey, selectType } = function () {
          if (key.includes("::")) {
            const [tempKey, tempType] = key.split("::")
            return { selectKey: tempKey, selectType: tempType }
          }
          return { selectKey: key, selectType: "string" }
        }()
        const selectValue = loadedParams[selectKey]
        if (selectValue === null || selectValue === undefined) {
          switch (selectType) {
            case "number":
              selectedValues[selectKey] = null
              break
            case "boolean":
              selectedValues[selectKey] = false
              break
            default: // as string
              selectedValues[selectKey] = ''
          }
        } else {
          switch (selectType) {
            case "number":
              const parsedNumberValue = parseFloat(selectValue)
              selectedValues[selectKey] = isNaN(parsedNumberValue) ? null : parsedNumberValue
              break
            case "boolean":
              selectedValues[selectKey] = selectValue === "false" ? false : Boolean(selectValue)
              break
            default: // as string
              selectedValues[selectKey] = selectValue
          }
        }
        return
      }
      console.warn(`loadParams key must be string key => ${key}`)
    })
    return selectedValues
  } else {
    return loadedParams
  }
}

export function diffObject(a, b) {
  return deepDiff(a, b) || []
}

export function diffParams(params, data) {
  const diffParamsResult = []
  diffObject(params, data).forEach((diffInfo) => {
    const { kind, path, lhs, rhs } = diffInfo
    if (kind === "N" && typeof rhs === "string" && rhs.trim() === "") {
      return
    }
    diffParamsResult.push(diffInfo)
  })
  return diffParamsResult
}

export function visit(locationHref) {
  Turbo.visit(locationHref)
}

// timeoutPromise(3000).then(()=>{}) // 3초기다림
export const timeoutPromise = function (wait = 0) {
  const time = Date.now()
  if (typeof wait !== 'number') {
    throw new Error('timeoutPromise::wait time must be number')
  }
  return new Promise(resolve => setTimeout(() => resolve({ wait, time, now: Date.now() }), wait))
}


export const baseKits = {
  // official
  asArray,
  deepClone,
  selectElement,
  selectElements,
  selectParentElement,
  dispatchElement,
  handleElement,
  customDataset,
  generateUUID,
  equalContents,
  turboFrameTransform,
  turboFrameReload,
  turboLocationReload,
  useChannel: actionCableHelper,
  formToFetchConfig: formToFetchConfig,
  jsonFetch,
  htmlFetch,
  turboRequest,
  useCatchResponseError,
  xValue: valueOfXNode,
  xValueSet: setValueOfXNode,
  useVisibility,
  useInputComposition,
  useAttributesObserver,
  visit(visitUrl) {
    Turbo.visit(visitUrl)
  },
  plainFormData,
  railsFormData(formSelector) {
    return railsParamsStyleToPlainData(plainFormData(formSelector))
  },
  loadParams,
  doit,
  begin,
  reflect,
  setSession,
  getSession,
  getSessionJSON,
  setSessionJSON,
  removeSession,
  setLocal,
  getLocal,
  setLocalJSON,
  getLocalJSON,
  removeLocal,
  mergeIfEmptyIn,
  mergeIfEmpty,
  mergeIfUndefinedIn,
  mergeIfUndefined,
  timeoutPromise,
  constants,
  debounce,
  parseHTML,
  vRenderNode,
  vRenderHTML,
  html(strings, ...values) {
    // html 하이라이트를 위해 사용
    return strings.reduce(
      (result, str, i) => result + str + (values[i] !== undefined ? values[i] : ''),
      ''
    );
  },
  downflow,
  upflow,
  // provideElement/consumeElement: downflow/upflow 개선판 (DOM 트리 기반 Provider-Consumer 패턴)
  provideElement,
  consumeElement,
  async rejectFetchResponse(response) {
    try {
      const generatedError = await begin(async () => {
        if (response && response.status && response.text && response.json) {
          return response.json().then((json) => {
            if (json && json.message) {
              return new Error(json.message)
            } else {
              return new Error(response.statusText || "Unknown error")
            }
          }).catch(() => {
            return response.text().then((text) => {
              return new Error(`${response.status} ${response.statusText}\n\n ${text}`)
            }).catch(() => {
              return new Error(`${response.status} ${response.statusText}`)
            })
          })
            .then((error) => {
              error.response = response
              error.status = response.status
              return error
            })
        }
        if (response && response.message) {
          const error = new Error(response.message)
          error.response = response
          return error
        }
        return new Error("Unknown error")
      })
      return Promise.reject(generatedError)
    } catch (error) {
      return Promise.reject(error)
    }
  },
  queryStringify,
  queryStringParse,
  queryStringAssign,
  kst,
  asLuxon,
  // application_helper compatibility
  distanceOfTimeInWordsWithDirection,
  // application_helper compatibility
  distanceOfDateInWordsWithDirection,
  // application_helper compatibility
  labelOfValueOptions(value, options, labelName = "label") {
    const found = options.find(it => (it.value ?? it["value"]) === value)
    return found ? found[labelName] : ""
  },
  transactionHelper,
}

export const vueKits = {
  ...baseKits,
  reactive,
}

export function withBaseKit(callback = (a) => a) {
  return callback(baseKits)
}

// deprecated ( withBaseKit() 사용 권장 )
export function baseKit(callback = (a) => a) {
  return callback(baseKits)
}

export function xDataKit(modules, extendKitOptions) {
  let alpineInstanceProxy = null

  const useAfterInit = {
    setup: false,
    events: [],
    register(fn) {
      if (typeof fn !== "function") return
      if (useAfterInit.setup) {
        fn.call(alpineInstanceProxy, alpineInstanceProxy)
      }
      useAfterInit.events.push(fn);
    }
  }

  const unloadEvent = {
    events: [
      () => {
        window.removeEventListener("turbo:visit", unloadEvent.handler)
      }
    ],
    handler(event) {
      unloadEvent.events.forEach((fn) => {
        if (typeof fn === "function") {
          fn.call(alpineInstanceProxy, event, alpineInstanceProxy)
        }
      })
    },
    register(fn) {
      if (typeof fn === "function") unloadEvent.events.push(fn)
    },
    setup() {
      setTimeout(() => {
        window.addEventListener("turbo:visit", unloadEvent.handler)
      }, 0)
    },
  }

  const beforeUnloadEvent = {
    events: [],
    handler(event) {
      beforeUnloadEvent.events.forEach((fn) => {
        if (typeof fn === "function") {
          fn.call(alpineInstanceProxy, event, alpineInstanceProxy)
        }
      })
    },
    register(fn) {
      if (typeof fn === "function") beforeUnloadEvent.events.push(fn);
    },
    setup() {
      document.addEventListener("turbo:before-visit", beforeUnloadEvent.handler)
      window.addEventListener("beforeunload", beforeUnloadEvent.handler)
      unloadEvent.register(() => {
        document.removeEventListener("turbo:before-visit", beforeUnloadEvent.handler)
        window.removeEventListener("beforeunload", beforeUnloadEvent.handler)
      })
    },
  }

  // TODO: 테스트가 안됨
  const turboSubmitEvent = {
    setup: false,
    startEvents: [],
    endEvents: [],
    handlers: {
      setup() {
        if (turboSubmitEvent.setup === false) {
          turboSubmitEvent.setup = true
          document.addEventListener("turbo:submit-start", turboSubmitEvent.handlers.start)
          document.addEventListener("turbo:submit-end", turboSubmitEvent.handlers.end)
          unloadEvent.register(() => {
            document.removeEventListener("turbo:submit-start", turboSubmitEvent.handlers.start)
            document.removeEventListener("turbo:submit-end", turboSubmitEvent.handlers.end)
          })
        }
      },
      start(event) {
        turboSubmitEvent.startEvents.forEach(([selector, eventHandler]) => {
          if (selector === "*" || selectElement(selector) === event.target) {
            eventHandler.call(alpineInstanceProxy, alpineInstanceProxy, event)
          }
        })
      },
      end(event) {
        turboSubmitEvent.endEvents.forEach(([selector, eventHandler]) => {
          if (selector === "*" || selectElement(selector) === event.target) {
            eventHandler.call(alpineInstanceProxy, alpineInstanceProxy, event)
          }
        })
      }
    },
    register(selector, callbackEvents) {
      if (typeof callbackEvents === "function") {
        callbackEvents = callbackEvents()
      }
      if (isPlainObject(callbackEvents)) {
        if (typeof callbackEvents.enter === "function") {
          turboSubmitEvent.handlers.setup()
          turboSubmitEvent.startEvents.push([selector, callbackEvents.enter])
        }
        if (typeof callbackEvents.exit === "function") {
          turboSubmitEvent.handlers.setup()
          turboSubmitEvent.endEvents.push([selector, callbackEvents.exit])
        }
      }
    }
  }

  if (typeof modules === "function") {
    // helepr
    const alpineHelperKits = {
      ...baseKits,
      // flowbite modal
      useModal(modalSelector, options = {}) {
        if (!options.placement) options.placement = "top"
        if (!options.backdrop) options.backdrop = "static" // or "dynamic"
        if (!options.closable) options.closable = false
        if (!options.backdropClasses) options.backdropClasses = "modal-backdrop fixed inset-0"

        const modalElement = selectElement(modalSelector)
        if (!modalElement) {
          throw new Error(`useModal() ${modalSelector} 에 해당하는 엘리먼트 요소가 없습니다.`)
        }
        if (!modalElement.id) {
          modalElement.id = `use-modal-${generateUUID('xxxx-yxxx')}`
        }
        return new FlowbiteModal(modalElement, options)
      },
      useTooltip(tooltipEl, triggerEl, options = {}) {
        if (!options.placement) options.placement = "top"
        if (!options.triggerType) options.triggerType = "hover"
        return new FlowbiteTooltip(tooltipEl, triggerEl, options)
      },
      // 여기부터는 alpine 디팬던시가 있습니다
      useMount(fn) {
        if (typeof fn === "function") {
          fn.call(alpineInstanceProxy)
        }
      },
      useForm: (formElementSelector, formEventCallbacks) => {
        const formController = formHelper(formElementSelector, formEventCallbacks)
        beforeUnloadEvent.register(() => { formController.destroy() })
        return formController
      },
      useAttributesObserver: (selector, callback) => {
        const attributesController = useAttributesObserver(selector, callback)
        beforeUnloadEvent.register(() => { attributesController.destroy() })
        return attributesController
      },
      useVisibility: (selector, callback) => {
        const visibilityController = useVisibility(selector, callback)
        beforeUnloadEvent.register(() => { visibilityController.destroy() })
        return visibilityController
      },
      // useDomRemove: domRemoveEvent.register,
      useAfterInit: useAfterInit.register,
      useBeforeUnload: beforeUnloadEvent.register,
      useUnload: unloadEvent.register,
      // useTurboCapture: turboCaptureEvent.register,
      useTurboSubmit: turboSubmitEvent.register,
    }
    modules = modules(Object.assign(alpineHelperKits, extendKitOptions?.addKits))
  }
  if (isPlainObject(modules)) {
    modules = [modules]
  }
  if (!Array.isArray(modules)) {
    console.warn("xDataKit require array or object defines", { modules })
    modules = []
  }
  if (!Alpine) {
    throw new Error("xDataKit must be called after Alpine is loaded")
  }
  const moduleData = {}

  // lifecycle 훅 병합 지원
  // - init: 자동 호출됨 (Alpine/petite-vue 컴포넌트 초기화 시)
  // - 그 외 hooks: 병합된 함수로 moduleData에 할당
  const mergeLifecycleHooks = extendKitOptions?.mergeLifecycleHooks || []
  const autoInvokeHooks = ["init"] // 자동 호출되는 훅 목록
  const allMergeableHooks = [...new Set([...autoInvokeHooks, ...mergeLifecycleHooks])]
  const lifecycleCollectors = {}
  allMergeableHooks.forEach(hookName => {
    lifecycleCollectors[hookName] = []
  })

  modules.forEach(module => {
    Object.keys(module).forEach(key => {
      if (allMergeableHooks.includes(key)) {
        // lifecycle 훅 수집 (init 포함)
        if (typeof module[key] === "function") {
          lifecycleCollectors[key].push(module[key])
        } else if (key === "init") {
          console.warn(`xDataKit module ${key} must be a function`)
        }
      } else {
        if (key === "$init") {
          throw new Error("xDataKit module $init is reserved")
        } else if (moduleData.hasOwnProperty(key)) {
          console.warn(`xDataKit module ${key} already defined`)
        } else {
          // getter/setter 감지 및 복사
          const descriptor = Object.getOwnPropertyDescriptor(module, key)
          if (descriptor && (descriptor.get || descriptor.set)) {
            Object.defineProperty(moduleData, key, descriptor)
          } else if (isPlainObject(module[key])) {
            moduleData[key] = Alpine.reactive(module[key])
          } else {
            moduleData[key] = module[key]
          }
        }
      }
    })
  })

  // 수집된 lifecycle 훅들을 병합된 함수로 변환 (init 제외 - init은 별도 처리)
  mergeLifecycleHooks
    .filter(hookName => !autoInvokeHooks.includes(hookName))
    .forEach(hookName => {
      const hooks = lifecycleCollectors[hookName]
      if (hooks.length > 0) {
        moduleData[hookName] = function (...args) {
          hooks.forEach(hook => {
            try {
              hook.call(this, ...args)
            } catch (error) {
              console.error(`Error in ${hookName} hook:`, error)
            }
          })
        }
      }
    })

  // init 훅은 자동 호출 형태로 설정
  const initializers = lifecycleCollectors["init"] || []

  moduleData.$init = false
  moduleData.init = function () {
    const self = extendKitOptions?.context || this
    const initResults = []

    initializers.forEach(initializer => {
      try {
        const initResult = initializer.call(self, self)
        initResults.push(initResult)
      } catch (error) {
        console.error(error)
        throw error
      }
    })

    alpineInstanceProxy = self
    // domRemoveEvent.setup()
    unloadEvent.setup()
    beforeUnloadEvent.setup()

    return Promise.all(initResults).then(() => {
      self.$init = true
      useAfterInit.setup = true
      useAfterInit.events.forEach((fn) => {
        fn.call(self, self)
      })
    })
  }
  // console.log('moduleData', moduleData)
  return moduleData
}

export function vScopeKit(modules) {
  return xDataKit(modules, {
    addKits: {
      reactive,
    },
    mergeLifecycleHooks: ["mounted", "unmounted"]
  })
}

// 알파인과 유사한 동작하는 오브젝트를 반환합니다
export async function xScriptKit(modules, context = {}) {
  if (typeof modules !== "function") {
    console.error("xScriptKit require function argument[0]", { modules, context })
    throw new Error("xScriptKit require function argument[0]")
  }
  const alphaData = xDataKit((utils) => {
    return { init() { modules(utils) } }
  }, { context: context })
  const alphaInit = alphaData.init
  const baseContext = Object.assign(context, { ...alphaData, init: null })
  alphaInit.call(baseContext, baseContext)
  return baseContext
} 
